import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService, MessageService } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subject, takeUntil } from 'rxjs';
import { AdminService } from 'src/app/services/admin.service';
import { SecretService } from 'src/app/services/secret.service';
import { SocketService } from 'src/app/services/socket.service';
import { environment } from 'src/environments/environment';

interface Notification {
  _id: string;
  title: string;
  body: string;
  url: string;
  createdAt: string;
  initiatedBy: {
    fullName: string;
  };
  read: boolean;
}

@Component({
  selector: 'app-notification-show',
  templateUrl: './notification-show.component.html',
  styleUrls: ['./notification-show.component.scss'],
  standalone: false,
})
export class NotificationShowComponent implements OnInit, OnDestroy {
  unReadCount: number = 0;
  notificationList: Notification[] = [];
  role_number!: number;
  user_id: string;
  url = environment.adminUrl;
  private unsubscribe$ = new Subject<void>();

  @ViewChild('notifyAll') notifyAll!: OverlayPanel;

  constructor(
    private adminService: AdminService,
    private router: Router,
    private toastr: ToastrService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private secretService: SecretService,
    private socketService: SocketService
  ) {
    const rawUserId = localStorage.getItem('user_id');
    this.user_id = rawUserId
      ? this.secretService.decryptLocalStorageData(rawUserId) ?? ''
      : '';
    this.role_number = Number(
      this.secretService.decryptLocalStorageData(
        localStorage.getItem('role_number')
      )
    );
    this.notificationList = [];
  }

  ngOnInit(): void {
    // Always try connect
    this.socketService.connect(this.user_id);

    // Load unread notifications on init
    this.showAllNotification();

    // Listen for real-time notifications
    this.socketService.off('notification'); // avoid duplicate bindings
    this.socketService.onNotification((notification: Notification) => {
      if (notification && !this.isDuplicateNotification(notification)) {
        this.notificationList.unshift(notification);
        this.unReadCount += 1;
      }
    });
  }

  onBellClick(event: Event): void {
    if (this.notifyAll) {
      this.notifyAll.toggle(event);
      if (this.notificationList.length === 0) {
        this.showAllNotification();
      }
    }
  }

  isDuplicateNotification(newNotification: Notification): boolean {
    return this.notificationList.some((n) => n._id === newNotification._id);
  }

  showAllNotification(): void {
    this.adminService.getAllNotifications(this.user_id).subscribe(
      (res: { data: Notification[] }) => {
        this.notificationList = res?.data || [];
        this.unReadCount = this.notificationList.filter((n) => !n.read).length;
      },
      (err) => {
        console.error('Error fetching notification data:', err);
      }
    );
  }

  onNotificationClick(notification: Notification): void {
    const id = notification._id;
    this.adminService.markNotificationAsRead(id).subscribe(
      () => {
        notification.read = true;
        this.unReadCount = this.notificationList.filter((n) => !n.read).length;
      },
      (err) => {
        console.error('Error marking notification as read:', err);
      }
    );
  }

  clearAllNotiffications(): void {
    this.adminService.markAllNotificationsAsRead().subscribe(
      () => {
        this.notificationList = [];
        this.unReadCount = 0;
      },
      (err) => {
        console.error('Error marking all notifications as read:', err);
      }
    );
  }

  ngOnDestroy(): void {
    this.socketService.disconnect(); // full cleanup
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
